import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { MobxComponent } from '../../../../../mobx/components';
import { openDeferredWindow } from '../../../../utils/open_deferred_window';

interface Props {
  children: React.ReactNode;
}

// Design tokens from the Loyalty Points Figma file.
export const LC = {
  red: '#fb2f2f',
  redSoft: '#fff3f3',
  border: '#dfdfdf',
  cardBorder: '#ecece1',
  metricBorder: '#e9eaeb',
  rowBorder: '#f0f1f3',
  white: '#ffffff',
  offWhite: '#f4f5f6',
  tile: '#f0f4f6',
  title: '#313131',
  label: '#4b4f5e',
  muted: '#6b7270',
  tertiary: '#535862',
  strong: '#181d27',
  success: '#009100',
};

export const loyaltyInputStyle: React.CSSProperties = {
  width: '100%',
  background: '#fefefe',
  border: `1px solid ${LC.border}`,
  borderRadius: 4,
  padding: 12,
  fontSize: 16,
  lineHeight: '24px',
  color: LC.label,
  outline: 'none',
};

export const LoyaltyCard = (props: {
  children?: React.ReactNode;
  style?: React.CSSProperties;
  padding?: number;
}) => (
  <div
    style={{
      background: LC.white,
      border: `1px solid ${LC.cardBorder}`,
      borderRadius: 8,
      padding: props.padding === undefined ? 24 : props.padding,
      ...props.style,
    }}
  >
    {props.children}
  </div>
);

export const LoyaltyCardHeading = (props: {
  title: string;
  description?: string;
  right?: React.ReactNode;
}) => (
  <div className="flex-line flex-wrap" style={{ gap: 16, alignItems: 'center', marginBottom: 24 }}>
    <div style={{ flex: '1 0 0', minWidth: 200 }}>
      <div style={{ fontSize: 16, lineHeight: '32px', fontWeight: 600, color: '#000' }}>{props.title}</div>
      {props.description && (
        <div style={{ fontSize: 12, lineHeight: '24px', color: LC.muted, marginTop: 8 }}>{props.description}</div>
      )}
    </div>
    {props.right}
  </div>
);

/** Label on the left (240px), control on the right (420px) — matches the Figma settings rows. */
export const LoyaltyFieldRow = (props: { label: string;  width?: number | string; isModal?: boolean; children?: React.ReactNode }) => (
  <div className="flex-line flex-wrap" style={{ gap: 24, alignItems: 'flex-start' }}>
    <div style={{ width: props.width === undefined ? (props.isModal === true ? '56px' : '240px') : props.width }}>
      <span style={{ fontSize: 14, lineHeight: '24px', fontWeight: 500, color: LC.label }}>{props.label}</span>
    </div>
    <div style={{ width: props.width === undefined ? (props.isModal === true ? 'auto' : '420px') : props.width }}>{props.children}</div>
  </div>
);

/** Bordered input shell with optional prefix/suffix text, as used across the Figma forms. */
export const LoyaltyInputBox = (props: {
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  children?: React.ReactNode;
}) => (
  <div
    className="flex-line"
    style={{
      gap: 8,
      alignItems: 'center',
      background: '#fefefe',
      border: `1px solid ${LC.border}`,
      borderRadius: 4,
      padding: '0 12px',
    }}
  >
    {props.prefix && <span style={{ fontSize: 16, color: LC.muted }}>{props.prefix}</span>}
    <div style={{ flex: '1 0 0', minWidth: 0 }}>{props.children}</div>
    {props.suffix && <span style={{ fontSize: 16, color: LC.muted, whiteSpace: 'nowrap' }}>{props.suffix}</span>}
  </div>
);

export const loyaltyBorderlessInputStyle: React.CSSProperties = {
  width: '100%',
  border: 'none',
  outline: 'none',
  background: 'transparent',
  padding: '12px 0',
  fontSize: 16,
  lineHeight: '24px',
  color: LC.label,
};

/** Secondary (outlined) and primary buttons from the Figma footer / toolbars. */
export const LoyaltyButton = (props: {
  variant?: 'primary' | 'secondary' | 'ghost';
  onClick?: () => void;
  type?: 'button' | 'submit';
  disabled?: boolean;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  id?: string;
}) => {
  const variant = props.variant || 'secondary';
  const base: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '6px 12px',
    borderRadius: 8,
    fontSize: 12,
    lineHeight: '20px',
    fontWeight: 500,
    cursor: props.disabled ? 'not-allowed' : 'pointer',
    opacity: props.disabled ? 0.6 : 1,
  };
  const variants: { [key: string]: React.CSSProperties } = {
    primary: { background: LC.red, color: LC.offWhite, border: `1px solid ${LC.red}` },
    secondary: { background: LC.offWhite, color: LC.tertiary, border: '1px solid rgba(83,88,98,0.2)' },
    ghost: { background: 'transparent', color: LC.tertiary, border: '1px solid transparent' },
  };
  return (
    <button
      id={props.id}
      type={props.type || 'button'}
      disabled={props.disabled}
      onClick={props.onClick}
      style={{ ...base, ...variants[variant], ...props.style }}
    >
      {props.children}
    </button>
  );
};

/** Pill segmented control (Graph / List). */
export const LoyaltySegmented = (props: {
  value: string;
  options: { label: string; value: string }[];
  onChange: (value: string) => void;
}) => (
  <div className="flex-line" style={{ alignItems: 'center' }}>
    {props.options.map(option => {
      const active = option.value === props.value;
      return (
        <button
          key={option.value}
          type="button"
          onClick={() => props.onChange(option.value)}
          style={{
            width: 96,
            padding: '8px 16px',
            border: 'none',
            borderRadius: 8,
            background: active ? LC.redSoft : 'transparent',
            color: active ? LC.red : LC.tertiary,
            fontSize: 14,
            lineHeight: '24px',
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

interface State {
  openingAdmin: boolean;
  adminError: string;
}

@inject('store')
@observer
export class LoyaltyShell extends MobxComponent<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { openingAdmin: false, adminError: '' };
  }

  /** Opens native-loyalty's own admin signed in as the current dashboard user, with the same
   * loyalty permissions they hold here (a fresh single-use link per click). */
  openLoyaltyAdmin = async () => {
    const { store } = this.injected;
    this.setState({ openingAdmin: true, adminError: '' });
    // Open the tab now, synchronously with the click, and navigate it once the handoff token
    // comes back — Safari silently blocks window.open() called after an await.
    const navigateAdminWindow = openDeferredWindow();
    try {
      const response = await store.api.native_loyalty_handoff_token({ restaurant_id: store.restaurant!._id });
      if (response.outcome) {
        this.setState({ openingAdmin: false, adminError: response.message || 'Failed to open Loyalty Admin' });
        return;
      }
      navigateAdminWindow(response.data.url);
      this.setState({ openingAdmin: false });
    } catch (e) {
      this.setState({ openingAdmin: false, adminError: 'An error occurred' });
    }
  };

  render() {
    const { store } = this.injected;
    const r = store.restaurant!;
    const pathname = store.router.s.path;
    const tabs = [
      { label: 'Dashboard', path: `/restaurant/${r._id}/loyalty` },
      { label: 'Members', path: `/restaurant/${r._id}/loyalty/members` },
      { label: 'Settings', path: `/restaurant/${r._id}/loyalty/settings` },
    ];

    return (
      <div>
        <div className="flex-line flex-wrap" style={{ gap: 0, marginBottom: 24, padding: '5px 24px', backgroundColor: LC.white }}>
          {tabs.map(tab => {
            const active = pathname === tab.path;
            return (
              <button
                key={tab.path}
                type="button"
                onClick={() => store.router.push(tab.path)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: active ? LC.red : LC.label,
                  borderRadius: 4,
                  padding: 12,
                  fontSize: 12,
                  lineHeight: '24px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {tab.label}
              </button>
            );
          })}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
            {this.state.adminError && (
              <span style={{ fontSize: 14, color: LC.red }}>{this.state.adminError}</span>
            )}
            <LoyaltyButton onClick={this.openLoyaltyAdmin} disabled={this.state.openingAdmin}>
              {this.state.openingAdmin ? 'Opening…' : 'Open Loyalty Admin'}
            </LoyaltyButton>
          </div>
        </div>
        <div className="flex-col" style={{ display: 'flex', flexDirection: 'column', gap: 24, paddingLeft: 24, paddingRight: 24 }}>
          {this.props.children}
        </div>
      </div>
    );
  }
}
